import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, KeyRound, Copy, Download, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import InstallAppCard from "@/components/InstallAppCard";

const ALPS = "https://images.pexels.com/photos/8991530/pexels-photo-8991530.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

// Gemeinsames Auth-System, zwei textlich getrennte Zugänge (Design identisch).
const VARIANTS = {
  admin: {
    pill: "Intern · KEWAG-Team",
    title: "KEWAGPage Control OS",
    subtitle: "Websites, Anfragen, Kunden-Cockpits und Systeme zentral steuern.",
    heroTitle: ["Ein Betriebssystem.", "Viele Kunden.", "Volle Kontrolle."],
    heroText: "Das zentrale Control OS für KEWAG & Partner GmbH. Websites, Anfragen und Kunden-Cockpits an einem Ort.",
    google: true,
    forgot: true,
    trust: ["Verschlüsselte Verbindung", "2FA-geschützt"],
    crossPrompt: "Sind Sie Kunde?",
    crossLabel: "Zum Website-Cockpit",
    crossTo: "/login",
  },
  cockpit: {
    pill: "Kunden-Zugang",
    title: "Website-Cockpit",
    subtitle: "Ihre Website, offenen Punkte, Anfragen, Aufgaben, Support und Rechnungen einfach im Blick.",
    heroTitle: ["Ihre Website.", "Ihre Anfragen.", "Alles im Blick."],
    heroText: "Ihr persönliches Website-Cockpit: Website, Anfragen, Aufgaben, Support und Rechnungen an einem Ort.",
    google: false,
    forgot: true,
    trust: ["Verschlüsselte Verbindung", "Geschützter Kundenbereich"],
    crossPrompt: "Gehören Sie zum KEWAG-Team?",
    crossLabel: "Zum Control-OS-Login",
    crossTo: "/admin/login",
  },
};

// Offizielles weisses KEWAGPage-Logo (PNG, transparent, randlos) — exakt wie geliefert, ohne Box, ohne Filter/Invert.
// Server meldet so eine abgelaufene/ungueltige 2FA-Anmeldesitzung (10 Min.); Code-Eingabe ist dann zwecklos.
const EXPIRED_2FA = ["2FA-Sitzung abgelaufen. Bitte erneut anmelden.", "Ungültiges 2FA-Token",
  "2FA nicht aktiv. Bitte erneut anmelden.", "2FA-Einrichtung nicht gefunden. Bitte erneut anmelden."];

const InlineError = ({ testid, children }) => (
  <div role="alert" data-testid={testid} className="flex items-start gap-2 rounded-lg border border-red-400/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
    <AlertTriangle className="w-4 h-4 shrink-0 mt-px text-red-300" /><span>{children}</span>
  </div>
);

const Brand = ({ className = "", w = "w-[200px]" }) => (
  <img src="/brand/kewagpage-logo-white.png" alt="KEWAGPage" data-testid="brand-logo" draggable="false"
    className={`${w} h-auto object-contain block select-none ${className}`} />
);

export default function Login({ variant = "admin" }) {
  const v = VARIANTS[variant] || VARIANTS.admin;
  const { login, complete2fa, recover2fa, forgotPassword, user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null); // {pending_token, setup, qr, secret}
  const [code, setCode] = useState("");
  const [recoverMode, setRecoverMode] = useState(false);
  const [recoverPw, setRecoverPw] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]); // Einmal-Anzeige nach erzwungener 2FA-Einrichtung
  const [savedUser, setSavedUser] = useState(null);
  const [err, setErr] = useState(null); // {scope: "login"|"code"|"recover", text, expired}
  const holdNavRef = useRef(false); // haelt die Auto-Weiterleitung an, solange Backup-Codes gezeigt werden

  useEffect(() => {
    if (user && !holdNavRef.current) nav(["client", "client_staff"].includes(user.role) ? "/cockpit" : "/admin", { replace: true });
  }, [user, nav]);

  // Nach Google-Login mit Team-Pflicht-2FA: hinterlegte Challenge aufnehmen und QR/Code-Schritt zeigen.
  useEffect(() => {
    const raw = sessionStorage.getItem("kewag_pending_2fa");
    if (raw) {
      sessionStorage.removeItem("kewag_pending_2fa");
      try {
        const p = JSON.parse(raw);
        if (p?.pending_token) { setTwofa(p); setCode(""); }
      } catch { /* ignorieren */ }
    }
  }, []);

  const goHome = (u) => nav(["client", "client_staff"].includes(u.role) ? "/cockpit" : "/admin", { replace: true });

  // Backend-Fehlertexte (Deutsch) sauber uebersetzen — inkl. dynamischer Rate-Limit-Meldung mit Minutenzahl.
  const trErr = (detail, fallback) => {
    if (!detail) return fallback;
    const m = detail.match(/Zu viele Fehlversuche.*?(\d+)\s*Minute/);
    if (m) return t("Zu viele Fehlversuche. Bitte in {n} Minute(n) erneut versuchen.").replace("{n}", m[1]);
    return t(detail) || fallback;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const data = await login(email, password);
      if (data.twofa_required) {
        setTwofa({ pending_token: data.pending_token, setup: data.twofa_setup, qr: data.qr, secret: data.secret });
        setCode("");
      } else if (data.user) {
        toast.success(t("welcome"));
        goHome(data.user);
      }
    } catch (err) {
      setPassword("");
      setErr({ scope: "login", text: trErr(err.response?.data?.detail, t("Anmeldung fehlgeschlagen")) });
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const path = twofa.setup ? "/auth/2fa/setup-verify" : "/auth/2fa/verify";
      if (twofa.setup) holdNavRef.current = true; // Weiterleitung anhalten, bis Codes gesichert sind
      const data = await complete2fa(path, twofa.pending_token, code);
      if (twofa.setup && data.backup_codes?.length) {
        // Erzwungene Einrichtung: 10 Einmal-Backup-Codes GENAU EINMAL anzeigen, bevor es weitergeht.
        setBackupCodes(data.backup_codes);
        setSavedUser(data.user);
        toast.success(t("2FA aktiviert. Bitte Backup-Codes sichern."));
      } else if (data.user) {
        holdNavRef.current = false;
        toast.success(twofa.setup ? t("2FA aktiviert. Willkommen!") : t("welcome"));
        goHome(data.user);
      }
    } catch (err) {
      holdNavRef.current = false;
      const detail = err.response?.data?.detail;
      setCode(""); // fehlgeschlagenen Code nie erneut senden
      setErr(EXPIRED_2FA.includes(detail)
        ? { scope: "code", expired: true, text: t("Die 2FA-Anmeldesitzung ist abgelaufen (nach 10 Minuten). Es wurde nichts geändert. Bitte melden Sie sich erneut an.") }
        : { scope: "code", text: trErr(detail, t("Code ungültig")) });
    } finally {
      setBusy(false);
    }
  };

  const saveCodesFile = (codes) => {
    const blob = new Blob([`KEWAGPage — 2FA Backup-Codes\n(jeder Code ist nur EINMAL gueltig — sicher aufbewahren)\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "kewagpage-2fa-backup-codes.txt"; a.click(); URL.revokeObjectURL(url);
  };
  const copyCodes = (codes) => { navigator.clipboard?.writeText(codes.join("\n")); toast.success(t("Backup-Codes kopiert")); };
  const finishBackupCodes = () => {
    holdNavRef.current = false;
    const u = savedUser;
    setBackupCodes([]); setSavedUser(null);
    if (u) goHome(u);
  };

  const resetToLogin = () => { setTwofa(null); setCode(""); setPassword(""); setRecoverMode(false); setRecoverPw(""); setErr(null); };

  const openForgot = () => { setForgotMode(true); setForgotSent(false); setForgotEmail(email); };
  const closeForgot = () => { setForgotMode(false); setForgotSent(false); setForgotEmail(""); };
  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(forgotEmail);
      setForgotSent(true); // immer neutrale Bestaetigung (keine Enumeration)
    } catch {
      setForgotSent(true); // auch bei Fehler neutral bleiben
    } finally {
      setBusy(false);
    }
  };

  const submitRecover = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const data = await recover2fa(email, recoverPw);
      setTwofa({ pending_token: data.pending_token, setup: data.twofa_setup, qr: data.qr, secret: data.secret });
      setCode(""); setRecoverMode(false); setRecoverPw("");
      toast.success(t("Neuer QR-Code erstellt. Bitte in Ihrer Authenticator-App neu einrichten."));
    } catch (err) {
      setRecoverPw("");
      setErr({ scope: "recover", text: trErr(err.response?.data?.detail, t("Wiederherstellung fehlgeschlagen")) });
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={ALPS} alt={t("Schweizer Alpen")} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Brand w="w-[200px]" className="self-start" />
          <div>
            <h2 className="text-4xl font-black tracking-tighter leading-tight" data-testid="login-hero-title">
              {v.heroTitle.map((line, i) => {
                const last = i === v.heroTitle.length - 1;
                return last
                  ? <span key={i} className="text-[#A3E635]">{t(line)}</span>
                  : <React.Fragment key={i}>{t(line)}<br /></React.Fragment>;
              })}
            </h2>
            <p className="mt-4 text-gray-400 max-w-md">{t(v.heroText)}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-in">
          <div className="flex items-center justify-between mb-8 gap-2">
            <Brand w="w-[150px]" className="lg:hidden" />
            <div className="flex gap-1 ml-auto">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${lang === l.code ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-sm p-6 sm:p-8 shadow-2xl shadow-black/40">
          {forgotMode ? (
            <div data-testid="forgot-panel" className="fade-in">
              <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
                <KeyRound className="w-5 h-5 text-[#A3E635]" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter">{t("Passwort zurücksetzen")}</h1>
              {!forgotSent ? (
                <>
                  <p className="text-gray-400 mt-1 mb-6 text-sm">
                    {t("Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.")}
                  </p>
                  <form onSubmit={submitForgot} className="space-y-4">
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t("email")}</label>
                      <input data-testid="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                        className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors"
                        placeholder="name@firma.ch" autoFocus />
                    </div>
                    <button data-testid="forgot-submit" type="submit" disabled={busy || !forgotEmail}
                      className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                      {busy ? "..." : t("Link zum Zurücksetzen senden")}
                    </button>
                  </form>
                </>
              ) : (
                <div data-testid="forgot-confirm" className="mt-2">
                  <div className="flex items-start gap-2 rounded-xl border border-[#A3E635]/30 bg-[#A3E635]/10 p-3 mb-4">
                    <ShieldCheck className="w-4 h-4 text-[#A3E635] shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-200">
                      {t("Falls ein Konto zu dieser E-Mail besteht, senden wir Ihnen einen Link zum Zurücksetzen des Passworts. Bitte prüfen Sie auch Ihren Spam-Ordner.")}
                    </p>
                  </div>
                </div>
              )}
              <button data-testid="forgot-back" onClick={closeForgot} className="mt-4 block text-xs text-gray-400 hover:text-white transition-colors">
                {t("← Zurück zur Anmeldung")}
              </button>
            </div>
          ) : !twofa ? (
            <>
              <span data-testid="login-area-badge" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/25 px-2.5 py-1 rounded-full">{t(v.pill)}</span>
              <h1 className="text-3xl font-black tracking-tighter mt-4" data-testid="login-title">{t(v.title)}</h1>
              <p className="text-gray-400 mt-2 mb-8 text-sm leading-relaxed" data-testid="login-subtitle">{t(v.subtitle)}</p>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t("email")}</label>
                  <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors"
                    placeholder="name@firma.ch" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t("password")}</label>
                  <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors"
                    placeholder="••••••••" />
                </div>
                {err?.scope === "login" && <InlineError testid="login-error">{err.text}</InlineError>}
                <button data-testid="login-submit" type="submit" disabled={busy}
                  className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {busy ? "..." : t("login")}
                </button>
              </form>

              {v.forgot && (
                <div className="mt-4 flex items-center justify-between gap-3 text-xs" data-testid="login-help">
                  <button type="button" onClick={openForgot} data-testid="login-forgot-password"
                    className="text-[#A3E635] hover:text-[#BEF264] font-medium whitespace-nowrap">{t("Passwort vergessen?")}</button>
                  <a href="mailto:info@kewagpage.ch?subject=Login-Problem" data-testid="login-support"
                    className="text-gray-400 hover:text-white text-right">{t("Probleme beim Login? KEWAG Support kontaktieren")}</a>
                </div>
              )}

              {v.google && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs text-gray-500">{t("or")}</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <button data-testid="login-google" onClick={googleLogin}
                    className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#fff" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.9a5 5 0 0 1-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-7.9z"/><path fill="#fff" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2v2.8A11 11 0 0 0 12 23z"/><path fill="#fff" d="M5.7 14.1a6.6 6.6 0 0 1 0-4.2V7.1H2a11 11 0 0 0 0 9.8z"/><path fill="#fff" d="M12 5.4c1.6 0 3 .6 4.2 1.7l3.1-3.1A11 11 0 0 0 2 7.1l3.7 2.8C6.6 7.3 9.1 5.4 12 5.4z"/></svg>
                    {t("loginGoogle")}
                  </button>
                </>
              )}
              <p className="mt-7 pt-5 border-t border-white/[0.08] text-center text-xs text-gray-500" data-testid="login-cross-link">
                {t(v.crossPrompt)}{" "}
                <Link to={v.crossTo} className="text-[#A3E635] hover:text-[#BEF264] font-semibold" data-testid="login-cross-link-btn">{t(v.crossLabel)}</Link>
              </p>
            </>
          ) : backupCodes.length > 0 ? (
            <div data-testid="twofa-setup-backupcodes" className="fade-in">
              <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
                <KeyRound className="w-5 h-5 text-[#A3E635]" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter">{t("Ihre Backup-Codes")}</h1>
              <p className="text-gray-400 mt-1 mb-4 text-sm">
                {t("2FA ist aktiv. Bewahren Sie diese Codes sicher auf — jeder Code funktioniert nur EINMAL und wird nur jetzt angezeigt.")}
              </p>
              <div className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 mb-4">
                <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200">{t("Ohne Authenticator melden Sie sich mit einem dieser Codes an. Danach sind sie verbraucht.")}</p>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="twofa-setup-backupcodes-list">
                {backupCodes.map((c) => (
                  <code key={c} className="text-center font-mono text-sm text-white bg-black border border-white/10 rounded-lg py-2">{c}</code>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => copyCodes(backupCodes)} data-testid="twofa-setup-codes-copy"
                  className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2"><Copy className="w-4 h-4" /> {t("Kopieren")}</button>
                <button onClick={() => saveCodesFile(backupCodes)} data-testid="twofa-setup-codes-download"
                  className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2"><Download className="w-4 h-4" /> {t("Herunterladen")}</button>
              </div>
              <button onClick={finishBackupCodes} data-testid="twofa-setup-codes-saved-btn"
                className="mt-4 w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors">
                {t("Ich habe die Codes sicher gespeichert")}
              </button>
            </div>
          ) : (
            <div data-testid="twofa-panel" className="fade-in">
              <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
                <KeyRound className="w-5 h-5 text-[#A3E635]" />
              </div>
              <h1 className="text-2xl font-black tracking-tighter">{twofa.setup ? t("2FA einrichten") : t("Zwei-Faktor-Login")}</h1>
              <p className="text-gray-400 mt-1 mb-6 text-sm">
                {twofa.setup
                  ? t("Scannen Sie den QR-Code mit einer Authenticator-App (z. B. Google oder Microsoft Authenticator) und geben Sie den 6-stelligen Code ein.")
                  : t("Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.")}
              </p>

              {twofa.setup && (
                <div className="mb-5">
                  <div className="bg-white p-3 rounded-xl inline-block">
                    <img data-testid="twofa-qr" src={twofa.qr} alt={t("2FA QR-Code")} className="w-44 h-44" />
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    {t("Kein Scan möglich? Schlüssel manuell eingeben:")}
                    <div data-testid="twofa-secret" className="mt-1 font-mono text-[11px] text-gray-200 break-all bg-white/5 border border-white/10 rounded px-2 py-1.5">{twofa.secret}</div>
                  </div>
                </div>
              )}

              {err?.scope === "code" && err.expired ? (
                <div className="space-y-3" data-testid="twofa-expired">
                  <InlineError testid="twofa-error">{err.text}</InlineError>
                  <button data-testid="twofa-expired-back" type="button" onClick={resetToLogin}
                    className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors">
                    {t("← Zurück zur Anmeldung")}
                  </button>
                </div>
              ) : (
              <form onSubmit={submitCode} className="space-y-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t("6-stelliger Code")}</label>
                  <input data-testid="twofa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); if (err?.scope === "code") setErr(null); }}
                    className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-gray-700 focus:outline-none focus:border-[#A3E635] transition-colors"
                    placeholder="000000" autoFocus />
                </div>
                <button data-testid="twofa-submit" type="submit" disabled={busy || code.length !== 6}
                  className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {busy ? "..." : (twofa.setup ? t("Aktivieren & anmelden") : t("Bestätigen"))}
                </button>
                {err?.scope === "code" && <InlineError testid="twofa-error">{err.text}</InlineError>}
              </form>
              )}

              {err?.expired ? null : !recoverMode ? (
                <button data-testid="twofa-lost" onClick={() => setRecoverMode(true)}
                  className="mt-4 text-xs text-[#A3E635]/90 hover:text-[#A3E635] transition-colors">
                  {t("Authenticator verloren? 2FA neu einrichten")}
                </button>
              ) : (
                <div data-testid="twofa-recover" className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.03]">
                  <p className="text-xs text-gray-400 mb-3">{t("Bestätigen Sie Ihr Passwort. Danach wird Ihr 2FA zurückgesetzt und ein neuer QR-Code angezeigt.")}</p>
                  <form onSubmit={submitRecover} className="space-y-3">
                    <input data-testid="twofa-recover-password" type="password" required value={recoverPw}
                      onChange={(e) => { setRecoverPw(e.target.value); if (err?.scope === "recover") setErr(null); }} placeholder={t("Passwort bestätigen")}
                      className="w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors" />
                    {err?.scope === "recover" && <InlineError testid="twofa-recover-error">{err.text}</InlineError>}
                    <div className="flex items-center gap-2">
                      <button data-testid="twofa-recover-submit" type="submit" disabled={busy || !recoverPw}
                        className="flex-1 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                        {busy ? "..." : t("2FA zurücksetzen")}
                      </button>
                      <button data-testid="twofa-recover-cancel" type="button" onClick={() => { setRecoverMode(false); setRecoverPw(""); setErr(null); }}
                        className="px-4 py-2.5 rounded-lg border border-white/10 text-gray-300 hover:text-white text-sm transition-colors">
                        {t("Abbrechen")}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {!err?.expired && (
                <button data-testid="twofa-back" onClick={resetToLogin} className="mt-4 block text-xs text-gray-400 hover:text-white transition-colors">
                  {t("← Zurück zur Anmeldung")}
                </button>
              )}
            </div>
          )}
          </div>

          <div data-testid="trust-badge" className="mt-6 flex items-center justify-center gap-4 text-[11px] text-gray-500">
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#A3E635]" /> {t(v.trust[0])}</span>
            <span className="text-gray-700">·</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#A3E635]" /> {t(v.trust[1])}</span>
          </div>

          {!forgotMode && !twofa && (
            <InstallAppCard appKey={variant === "admin" ? "control-os" : "cockpit"} />
          )}
        </div>
      </div>
    </div>
  );
}
